/**
 * Phase 6.4 — i18n scaffold (i18next + react-i18next).
 *
 * Initialises with two locales (en, en-GB) so the wiring is exercised
 * out of the box. Adding a new language is one new JSON file under
 * `locales/<code>.json` and an entry in `RESOURCES` below.
 *
 * Locale resolution order:
 *   1. ?locale=… query param (override for testing)
 *   2. user.locale from /auth/me (set on profile + persisted)
 *   3. browser's navigator.language
 *   4. fall back to 'en'
 *
 * Strings missing in the active locale fall back to `en` so a partial
 * translation never shows raw keys to users.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import enGB from "./locales/en-GB.json";

const RESOURCES = {
  "en":     { translation: en },
  "en-GB":  { translation: enGB },
} as const;

const SUPPORTED = Object.keys(RESOURCES) as Array<keyof typeof RESOURCES>;

function detectLocale(): string {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("locale");
  if (fromQuery && (SUPPORTED as string[]).includes(fromQuery)) return fromQuery;

  const stored = localStorage.getItem("control.locale");
  if (stored && (SUPPORTED as string[]).includes(stored)) return stored;

  const fromNav = navigator.language;
  if (fromNav && (SUPPORTED as string[]).includes(fromNav)) return fromNav;
  if (fromNav?.startsWith("en")) return "en";

  return "en";
}

void i18n
  .use(initReactI18next)
  .init({
    resources: RESOURCES,
    lng:         detectLocale(),
    fallbackLng: "en",
    interpolation: { escapeValue: false }, // React handles XSS escaping
    react:        { useSuspense: false },  // avoid Suspense bridge in early boot
  });

export function setLocale(code: string): void {
  if (!(SUPPORTED as string[]).includes(code)) return;
  localStorage.setItem("control.locale", code);
  void i18n.changeLanguage(code);
}

export function supportedLocales(): readonly string[] {
  return SUPPORTED;
}

export default i18n;
